<script setup lang="ts">
/**
 * BUY SLOTS — expand the sea-chest straight from the hero page. Self-contained
 * like QuickRecover: reads the tiered/capped slot terms from GET /shop and buys
 * 'inventory_slot' directly. Price climbs after 100 slots and the button
 * disables at the 200-slot ceiling. Emits 'updated' so the parent refetches.
 */
import { ref, computed, onMounted } from 'vue';
import { fetchShop, buyShopItem, type SlotTerms } from '../game/shop-api';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
// the runes icon replaced gold-bars in the 06.08 currency-icon fix
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import { t } from '../i18n';

const emit = defineEmits<{ (e: 'updated'): void }>();

const terms = ref<SlotTerms | null>(null);
const premium = ref(0);
const busy = ref(false);
const note = ref('');

async function load() {
  try {
    const v = await fetchShop();
    terms.value = v.slots;
    premium.value = v.balance.premium;
  } catch { /* control simply stays hidden */ }
}
onMounted(load);

const maxed = computed(() => !terms.value || terms.value.nextPrice == null);
const price = computed(() => terms.value?.nextPrice ?? 0);
const affordable = computed(() => premium.value >= price.value);

async function buy() {
  if (busy.value || maxed.value) return;
  const ok = await confirmAction({
    kicker: t('buySlots.kicker'),
    title: t('buySlots.title', { count: terms.value?.step ?? 5 }),
    icon: runesIcon,
    rows: [
      { label: t('buySlots.slots'), value: `${terms.value?.current ?? 0} → ${(terms.value?.current ?? 0) + (terms.value?.step ?? 5)}`, tone: 'dim' },
      { label: t('buySlots.price'), value: price.value.toLocaleString(), icon: runesIcon, tone: 'bright' },
    ],
    confirmLabel: t('buySlots.expand'),
  });
  if (!ok) return;
  busy.value = true; note.value = '';
  try {
    await buyShopItem('inventory_slot');
    toast(t('buySlots.toast', { count: terms.value?.step ?? 5 }), { icon: runesIcon });
    emit('updated');
    await load(); // refresh price (may cross into the higher tier) + balance
  } catch (e) {
    note.value = (e as Error).message; // short on bars, at max, …
    window.setTimeout(() => { note.value = ''; }, 4000);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <span v-if="terms" class="bs">
    <button
      v-if="!maxed"
      class="btn-forge btn-forge--sm bs-btn"
      :disabled="busy || !affordable"
      :title="affordable ? t('buySlots.titleAttr', { count: terms.step }) : t('buySlots.notEnough')"
      @click="buy"
    >
      <span>{{ busy ? '…' : `+${terms.step} slots` }}</span>
      <span class="bs-price"><img :src="runesIcon" alt="" class="w-3 h-3 object-contain shrink-0" />{{ price }}</span>
    </button>
    <span v-else class="bs-max" :title="`Sea-Chest is at the ${terms.max}-slot maximum`">{{ t('buySlots.maxed', { count: terms.max }) }}</span>
    <span v-if="note" class="bs-note">{{ note }}</span>
  </span>
</template>

<style scoped>
.bs { display: inline-flex; align-items: center; gap: 8px; }
.bs-btn { display: inline-flex; align-items: center; gap: 7px; }
/* Same plaque as QuickRecover's "Horn of Mead": the DEFAULT btn-forge is black
   lacquer with gold ink, so a gold price and the runes icon sit on it
   naturally. The --gold variant is brushed brass — gold on gold — which is what
   made both of them vanish here. */
.bs-price {
  display: inline-flex; align-items: center; gap: 3px;
  color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
}
.bs-max {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em; font-size: 10px;
  color: var(--color-text-dim); border: 1px solid var(--color-border); border-radius: 0;
  padding: 3px 10px;
}
.bs-note {
  font-family: var(--font-body, serif); font-style: italic; font-size: 11.5px;
  color: var(--color-error);
}
</style>
